import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  formatCurrency,
  formatMaybeCurrency,
  formatPercent,
} from '@shared/SharedAccountBody';
import type { StockChartRange } from '@shared/types';
import { PositionRow } from './PortfolioPositionsTable';
import {
  buildSignalForgeExplainUrl,
  resolveSignalForgeSymbol,
} from './signalForgeUtils';
import {
  usePortfolioSeries,
  usePositionTransactions,
  useStockBars,
  useStockProfile,
} from '../../queries/stockData';
import PortfolioStockChart, { type ChartMode } from './PortfolioStockChart';
import PortfolioStockDetails from './PortfolioStockDetails';
import PortfolioHoldingsCard, { type RealizedSummary } from './PortfolioHoldingsCard';
import PortfolioTransactionsTable from './PortfolioTransactionsTable';

type DrawerTab = 'price' | 'signal';

interface PortfolioStockDrawerProps {
  open: boolean;
  position: PositionRow | null;
  accountId: string | null;
  signalForgeEnabled: boolean;
  tradingStartedAt?: string;
  onClose: () => void;
}

function queryErrorMessage(error: unknown, fallback: string): string | null {
  if (!error) return null;
  return (
    (error as { response?: { data?: { error?: string } } }).response?.data
      ?.error ?? fallback
  );
}

const PortfolioStockDrawer = ({
  open,
  position,
  accountId,
  signalForgeEnabled,
  tradingStartedAt,
  onClose,
}: PortfolioStockDrawerProps) => {
  const [tab, setTab] = useState<DrawerTab>('price');
  const [range, setRange] = useState<StockChartRange>('1M');
  const [mode, setMode] = useState<ChartMode>('%');
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);

  const symbol = position?.symbol ?? null;
  const explainUrl = symbol ? buildSignalForgeExplainUrl(symbol) : '';
  const resolvedSymbol = symbol ? resolveSignalForgeSymbol(symbol) : '';

  const priceTabActive = open && tab === 'price';

  const {
    data: barsData,
    isFetching: barsLoading,
    error: barsError,
  } = useStockBars(accountId, symbol, range, priceTabActive);

  const { data: seriesData } = usePortfolioSeries(
    accountId,
    range,
    tradingStartedAt,
    priceTabActive,
  );

  const {
    data: transactionsData,
    isFetching: transactionsLoading,
    error: transactionsError,
  } = usePositionTransactions(accountId, symbol, priceTabActive);

  const {
    data: profile,
    isFetching: profileLoading,
    error: profileError,
  } = useStockProfile(accountId, symbol, priceTabActive);

  // Simple realized/exit P/L: each sell valued against the current avg entry.
  const realized = useMemo<RealizedSummary | undefined>(() => {
    const avgEntry = position?.avgEntryPrice;
    const fills = transactionsData?.transactions;
    if (!avgEntry || avgEntry <= 0 || !fills) return undefined;
    let soldQty = 0;
    let amount = 0;
    for (const tx of fills) {
      if (tx.side !== 'sell') continue;
      soldQty += tx.qty;
      amount += (tx.price - avgEntry) * tx.qty;
    }
    if (soldQty <= 0) return undefined;
    const costBasis = avgEntry * soldQty;
    return {
      amount: Math.round(amount * 100) / 100,
      percent: costBasis > 0 ? amount / costBasis : undefined,
      soldQty,
    };
  }, [transactionsData, position?.avgEntryPrice]);

  // Reset to the price tab whenever a different position is opened.
  useEffect(() => {
    if (open) setTab('price');
  }, [open, symbol]);

  // If SignalForge is turned off, never sit on the signal tab.
  useEffect(() => {
    if (!signalForgeEnabled && tab === 'signal') setTab('price');
  }, [signalForgeEnabled, tab]);

  useEffect(() => {
    if (tab !== 'signal') return;
    setIframeLoading(true);
    setIframeError(false);
  }, [tab, explainUrl]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  const handleBackdropClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) onClose();
    },
    [onClose],
  );

  if (!open || !position) return null;

  const plUp = position.unrealizedPL !== undefined && position.unrealizedPL >= 0;

  const pill = (tone: 'orange' | 'blue' | 'up' | 'down') =>
    ({
      orange: 'bg-[color:rgba(255,159,64,0.15)] text-[color:#ff9f40]',
      blue: 'bg-[color:rgba(56,182,255,0.15)] text-[color:#38b6ff]',
      up: 'bg-positive/15 text-positive',
      down: 'bg-negative/15 text-negative',
    })[tone];

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex animate-in fade-in justify-end bg-black/55 duration-200"
      role="presentation"
      onClick={handleBackdropClick}
    >
      <aside
        className="flex h-full w-[min(62vw,960px)] max-w-full animate-in slide-in-from-right flex-col border-l border-border bg-[color:var(--nq-surface-panel)] shadow-[var(--shadow-drawer)] duration-300"
        role="dialog"
        aria-modal="true"
        aria-labelledby="portfolio-stock-drawer-title"
      >
        <header className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-5 py-[18px]">
          <div className="min-w-0">
            <h2
              id="portfolio-stock-drawer-title"
              className="m-0 font-display text-[1.15rem] font-semibold text-foreground"
            >
              {profile?.name ? `${profile.name} (${resolvedSymbol})` : resolvedSymbol}
            </h2>
            {position.symbol !== resolvedSymbol && (
              <span className="mt-1 block text-xs text-muted-foreground">
                Broker: {position.symbol}
              </span>
            )}
            <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-2 text-[13px]">
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${pill('orange')}`}
              >
                {position.weightPct.toFixed(2)}% of portfolio
              </span>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${pill('blue')}`}
              >
                Invested {formatMaybeCurrency(position.marketValue)}
              </span>
              {position.unrealizedPL !== undefined && (
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${pill(plUp ? 'up' : 'down')}`}
                >
                  P/L {position.unrealizedPL >= 0 ? '+' : ''}
                  {formatCurrency(position.unrealizedPL)}
                  {position.unrealizedPLPct !== undefined &&
                    ` (${formatPercent(position.unrealizedPLPct)})`}
                </span>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2.5">
            <button
              type="button"
              className="grid size-9 cursor-pointer appearance-none place-items-center rounded-lg border border-border bg-muted p-0 text-2xl leading-none text-foreground/70 transition-[background,color] hover:bg-[color:rgba(255,255,255,0.12)] hover:text-foreground"
              onClick={onClose}
              autoFocus
              aria-label="Close stock panel"
            >
              ×
            </button>
          </div>
        </header>

        <div className="flex shrink-0 gap-1 border-b border-border px-5" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'price'}
            className={`cursor-pointer appearance-none border-0 border-b-2 bg-transparent px-3.5 py-3 text-[13px] font-semibold transition-[color,border-color] ${
              tab === 'price'
                ? 'border-b-primary text-foreground'
                : 'border-b-transparent text-muted-foreground hover:text-foreground/85'
            }`}
            onClick={() => setTab('price')}
          >
            Price
          </button>
          {signalForgeEnabled && (
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'signal'}
              className={`cursor-pointer appearance-none border-0 border-b-2 bg-transparent px-3.5 py-3 text-[13px] font-semibold transition-[color,border-color] ${
                tab === 'signal'
                  ? 'border-b-primary text-foreground'
                  : 'border-b-transparent text-muted-foreground hover:text-foreground/85'
              }`}
              onClick={() => setTab('signal')}
            >
              Signal Flow
            </button>
          )}
        </div>

        <div className="relative flex min-h-0 flex-1 flex-col">
          {tab === 'price' ? (
            <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 pt-[18px]">
              <PortfolioStockChart
                symbol={resolvedSymbol || position.symbol}
                bars={barsData?.bars}
                loading={barsLoading}
                error={queryErrorMessage(barsError, 'Failed to load price history')}
                range={range}
                onRangeChange={setRange}
                mode={mode}
                onModeChange={setMode}
                portfolioPoints={seriesData?.points}
                avgEntryPrice={position.avgEntryPrice}
                heldSince={position.heldSince}
                lastSoldAt={position.lastSoldAt}
              />
              <PortfolioHoldingsCard position={position} realized={realized} />
              <PortfolioTransactionsTable
                symbol={resolvedSymbol || position.symbol}
                transactions={transactionsData?.transactions}
                loading={transactionsLoading}
                error={queryErrorMessage(transactionsError, 'Failed to load transactions')}
                truncated={transactionsData?.truncated}
                avgEntryPrice={position.avgEntryPrice}
                heldQty={position.qty}
              />
              <PortfolioStockDetails
                symbol={resolvedSymbol || position.symbol}
                profile={profile}
                loading={profileLoading}
                error={queryErrorMessage(profileError, 'Failed to load details')}
              />
            </div>
          ) : (
            <>
              {iframeLoading && !iframeError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center text-sm text-muted-foreground">
                  <div
                    className="size-9 animate-spin rounded-full border-[3px] border-primary/20 border-t-primary"
                    aria-hidden="true"
                  />
                  <p>Loading signal flow graph…</p>
                </div>
              )}
              {iframeError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center text-sm text-muted-foreground">
                  <p>Could not load the SignalForge graph in this panel.</p>
                  <a
                    href={explainUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Open in new tab instead
                  </a>
                </div>
              )}
              <iframe
                key={explainUrl}
                src={explainUrl}
                title={`${resolvedSymbol} SignalForge explain`}
                className={`w-full flex-1 border-0 bg-[color:var(--nq-bg-darker)]${
                  iframeLoading ? ' absolute inset-0 opacity-0' : ''
                }`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                onLoad={() => setIframeLoading(false)}
                onError={() => {
                  setIframeLoading(false);
                  setIframeError(true);
                }}
              />
            </>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
};

export default PortfolioStockDrawer;
